import { Button } from '../../../../../../widgets/buttons';
import { Dialog } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { Select } from '../../../../../../widgets/select';
import React from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributeType,
    ComposerComparisonOperator,
    ComposerLogicalOperator
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

interface ComposerLinkAttributeNode {
    attributeID: string;
    comparisonOperator: ComposerComparisonOperator;
    optionID: string;
}
interface ComposerLinkOutput {
    logicalOperator: ComposerLogicalOperator;
    links: ComposerLinkAttributeNode[];
}
interface ComposerLinkAttributeDialogProps {
    currentAttribute: ComposerAttributeNode | null;
    modelAttributes: ComposerAttributeNode[];
    currentOption?: ComposerAttributeOptionNode;
    onConfirm: (attributes: ComposerLinkOutput) => void;
}
const ComposerSaveAttributeDialogWindow = styled(Dialog)`
    max-width: 600px;
`;
const Title = styled.h3`
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
`;

const ComposerLinkAttributeDialog: React.FC<ComposerLinkAttributeDialogProps> = ({ onConfirm, currentAttribute, modelAttributes, currentOption }) => {
    const operations = React.useMemo(
        () => [
            { label: T._('All of the following expressions are true', 'Admin'), value: ComposerLogicalOperator.AND },
            { label: T._('At least one of following expressions is true', 'Admin'), value: ComposerLogicalOperator.OR }
        ],
        []
    );

    const constraintOperations = React.useMemo(
        () => [
            { label: '=', value: ComposerComparisonOperator.EQUAL },
            { label: '≠', value: ComposerComparisonOperator.NOT_EQUAL }
        ],
        []
    );
    const [operation, setOperation] = React.useState<ComposerLogicalOperator>(
        currentOption?.linkOperation || currentAttribute?.linkOperation || ComposerLogicalOperator.AND
    );
    const attributesLinks =
        currentOption?.links?.map((l) => {
            return {
                attributeID: l.attributeID,
                comparisonOperator: l.operation,
                optionID: l.optionID
            } as ComposerLinkAttributeNode;
        }) ||
        currentAttribute?.links?.map((l) => {
            return {
                attributeID: l.attributeID,
                comparisonOperator: l.operation,
                optionID: l.optionID
            } as ComposerLinkAttributeNode;
        }) ||
        [];
    const [links, setLinks] = React.useState<ComposerLinkAttributeNode[]>([
        ...(attributesLinks?.length > 0
            ? attributesLinks
            : [
                  {
                      attributeID: '',
                      comparisonOperator: constraintOperations[0].value,
                      optionID: ''
                  }
              ])
    ]);

    const handleConfirm = () => {
        const link: ComposerLinkOutput = {
            logicalOperator: operation,
            links: links
        };
        onConfirm(link);
    };

    const shownAttributes = modelAttributes.filter((attr) => attr.type === ComposerAttributeType.Attribute && currentAttribute?.id !== attr.id);

    return (
        <Dialog windowDecorator={ComposerSaveAttributeDialogWindow} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <Title>{T._('Link Attributes', 'Admin')}</Title>
            <Container>
                <FormControl label={T._('Apply if', 'Admin')}>
                    <Select value={operation} onChange={(value) => setOperation(value as ComposerLogicalOperator)}>
                        {operations.map((op) => (
                            <option key={op.value} value={op.value}>
                                {op.label}
                            </option>
                        ))}
                    </Select>
                </FormControl>
                {links.map((attr, index) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <FormControl label={T._('Attribute', 'Admin')}>
                            <Select
                                value={attr.attributeID}
                                onChange={(value) => {
                                    const newAttributes = [...links];
                                    newAttributes[index].attributeID = value;
                                    newAttributes[index].optionID = shownAttributes.find((a: ComposerAttributeNode) => a.id === value)?.options[0]?.id || '';
                                    setLinks(newAttributes);
                                }}
                            >
                                <option value=''>{T._('Select an attribute', 'Admin')}</option>
                                {shownAttributes.map((a: ComposerAttributeNode) => (
                                    <option key={a.id} value={a.id}>
                                        {a.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl label={T._('Comparison Operator', 'Admin')}>
                            <Select
                                value={attr.comparisonOperator}
                                onChange={(value) => {
                                    const newAttributes = [...links];
                                    newAttributes[index].comparisonOperator = value as ComposerComparisonOperator;
                                    setLinks(newAttributes);
                                }}
                            >
                                {constraintOperations.map((op) => (
                                    <option key={op.value} value={op.value}>
                                        {op.label}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl label={T._('Option', 'Admin')}>
                            <Select
                                value={attr.optionID}
                                disabled={!attr.attributeID}
                                onChange={(e) => {
                                    const newAttributes = [...links];
                                    newAttributes[index].optionID = e;
                                    setLinks(newAttributes);
                                }}
                            >
                                {shownAttributes
                                    .find((a: ComposerAttributeNode) => a.id === attr.attributeID)
                                    ?.options.map((option: ComposerAttributeOptionNode) => (
                                        <option key={option.id} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                            </Select>
                        </FormControl>
                        <Button
                            type='button'
                            onClick={() => {
                                const newAttributes = links.filter((_, i) => i !== index);
                                if (newAttributes.length === 0) {
                                    newAttributes.push({
                                        attributeID: '',
                                        comparisonOperator: ComposerComparisonOperator.EQUAL,
                                        optionID: ''
                                    });
                                }
                                setLinks(newAttributes);
                            }}
                            style={{ marginLeft: 8 }}
                        >
                            {T._('Remove', 'Admin')}
                        </Button>
                        {index === links.length - 1 && (
                            <Button
                                type='button'
                                onClick={() => {
                                    setLinks([...links, { attributeID: '', comparisonOperator: ComposerComparisonOperator.EQUAL, optionID: '' }]);
                                }}
                                style={{ marginLeft: 8 }}
                            >
                                {T._('Add', 'Admin')}
                            </Button>
                        )}
                    </div>
                ))}
            </Container>
        </Dialog>
    );
};

export default ComposerLinkAttributeDialog;
